import React, { useMemo } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Badge,
  Tooltip,
  Center,
} from '@chakra-ui/react';
import { formatNameLastCommaFirst } from '../utils/clientNameDisplay';
import {
  buildLongevityEntries,
  getTimelineRange,
  getTimelinePosition,
  getYearMarkers,
} from '../utils/clientLongevityUtils';

const ROW_HEIGHT = 44;
const NAME_COLUMN_WIDTH = 200;
const TIMELINE_MIN_WIDTH = 640;

const ClientLongevityView = ({ clients, attendanceBounds = {}, onSelectClient }) => {
  const longevityEntries = useMemo(
    () => buildLongevityEntries(clients, attendanceBounds),
    [clients, attendanceBounds]
  );

  const { start: rangeStart, end: rangeEnd } = useMemo(
    () => getTimelineRange(longevityEntries),
    [longevityEntries]
  );

  const yearMarkers = useMemo(
    () => getYearMarkers(rangeStart, rangeEnd),
    [rangeStart, rangeEnd]
  );

  if (!longevityEntries.length) {
    return (
      <Center py={12}>
        <Text color="gray.500">No client start dates available for the longevity view.</Text>
      </Center>
    );
  }

  return (
    <Box borderWidth={1} borderRadius="md" overflow="hidden" bg="white">
      <Box overflowX="auto">
        <Box minW={`${NAME_COLUMN_WIDTH + TIMELINE_MIN_WIDTH}px`}>
          <HStack
            spacing={0}
            borderBottomWidth={1}
            bg="gray.50"
            position="sticky"
            top={0}
            zIndex={2}
          >
            <Box
              w={`${NAME_COLUMN_WIDTH}px`}
              flexShrink={0}
              px={4}
              py={3}
              borderRightWidth={1}
              bg="gray.50"
            >
              <Text fontSize="sm" fontWeight="bold" color="gray.600">
                Client
              </Text>
              <Text fontSize="xs" color="gray.500">
                Sorted by total active tenure
              </Text>
            </Box>
            <Box flex={1} position="relative" h="56px" px={2}>
              {yearMarkers.map((marker) => (
                <Box
                  key={marker.year}
                  position="absolute"
                  left={`${marker.position}%`}
                  top={0}
                  h="100%"
                  borderLeftWidth={1}
                  borderColor="gray.200"
                  pl={1}
                  pt={2}
                >
                  <Text fontSize="xs" fontWeight="semibold" color="gray.600">
                    {marker.year}
                  </Text>
                </Box>
              ))}
            </Box>
            <Box w="100px" flexShrink={0} px={3} py={3} borderLeftWidth={1} bg="gray.50">
              <Text fontSize="sm" fontWeight="bold" color="gray.600">
                Tenure
              </Text>
            </Box>
          </HStack>

          <VStack spacing={0} align="stretch">
            {longevityEntries.map((entry) => {
              const isActive = entry.client.status === 'Active';
              const tooltipLabel = [
                entry.periodsTooltip,
                `Total active: ${entry.tenureLabel}`,
                entry.hasReturned ? 'Inactive gap excluded between stints' : null,
              ]
                .filter(Boolean)
                .join(' · ');

              return (
                <HStack
                  key={entry.client.id}
                  spacing={0}
                  h={`${ROW_HEIGHT}px`}
                  borderBottomWidth={1}
                  borderColor="gray.100"
                  _hover={{ bg: 'gray.50' }}
                  cursor="pointer"
                  onClick={() => onSelectClient?.(entry.client)}
                >
                  <Box
                    w={`${NAME_COLUMN_WIDTH}px`}
                    flexShrink={0}
                    px={4}
                    py={2}
                    borderRightWidth={1}
                    overflow="hidden"
                  >
                    <Text fontSize="sm" fontWeight="medium" noOfLines={1}>
                      {formatNameLastCommaFirst(entry.client.name)}
                    </Text>
                    <HStack spacing={1} mt={0.5} flexWrap="wrap">
                      <Badge
                        size="sm"
                        colorScheme={isActive ? 'green' : 'gray'}
                      >
                        {entry.client.status}
                      </Badge>
                      {entry.hasReturned && (
                        <Badge size="sm" colorScheme="purple" variant="outline">
                          Returned
                        </Badge>
                      )}
                    </HStack>
                  </Box>

                  <Box flex={1} position="relative" h="100%" px={2}>
                    <Tooltip label={tooltipLabel} hasArrow placement="top">
                      <Box position="absolute" inset={0} px={0}>
                        {entry.periods.map((period, index) => {
                          const left = getTimelinePosition(period.start, rangeStart, rangeEnd);
                          const right = getTimelinePosition(period.end, rangeStart, rangeEnd);
                          const width = Math.max(right - left, 0.8);

                          return (
                            <Box
                              key={`${entry.client.id}-period-${index}`}
                              position="absolute"
                              top="50%"
                              transform="translateY(-50%)"
                              left={`${left}%`}
                              w={`${width}%`}
                              h="10px"
                              borderRadius="full"
                              bg={isActive ? 'blue.400' : 'gray.400'}
                              boxShadow="sm"
                              _hover={{ bg: isActive ? 'blue.500' : 'gray.500' }}
                            />
                          );
                        })}
                      </Box>
                    </Tooltip>
                  </Box>

                  <Box
                    w="100px"
                    flexShrink={0}
                    px={3}
                    borderLeftWidth={1}
                    display="flex"
                    alignItems="center"
                  >
                    <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                      {entry.tenureLabel}
                    </Text>
                  </Box>
                </HStack>
              );
            })}
          </VStack>
        </Box>
      </Box>
    </Box>
  );
};

export default ClientLongevityView;
